import { useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  isPricingConflict,
  PricingChannel,
  PricingConfigurationValues,
  PricingRate,
} from '@/services/Pricing/pricing-configuration.service';
import { computeNextRates } from './pricingConfigUtils';
import { formRowsToValues } from './pricingFormMapping';
import { parsePricingChannel } from './usePricingChannel';

type SubmitPromise = { unwrap: () => Promise<unknown> };

type UsePricingFormParams = {
  /** The inherited rates for the ACTIVE channel, keyed by country. */
  baseline: PricingConfigurationValues;
  /** This entity's saved rates for the ACTIVE channel, keyed by country. */
  existing: PricingConfigurationValues | undefined;
  /** Every stored rate across both channels — send the full list, untouched channel included. */
  existingRates: PricingRate[];
  channel: PricingChannel;
  submit: (nextRates: PricingRate[]) => SubmitPromise;
  messages: {
    success: string;
    error: string;
    /** On 409: retrying would resend the stale list, so this must tell the admin to reload. */
    conflict: string;
  };
};

export function usePricingForm({
  baseline,
  existing,
  existingRates,
  channel,
  submit,
  messages,
}: UsePricingFormParams) {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const fallbackCountry = Object.keys(existing ?? {})[0] ?? Object.keys(baseline)[0] ?? '';
  const activeCountry = selectedCountry ?? fallbackCountry;

  // Bumped on a confirmed save; the form re-seeds on it, which clears `isDirty`.
  const [savedAt, setSavedAt] = useState(0);

  const submitHandler = (data: Record<string, any>) => {
    // Read from the form data, not the prop, so the save lands on the channel that was rendered.
    const submittedChannel = parsePricingChannel(data.channel) ?? channel;
    if (!activeCountry) return;
    const values = formRowsToValues(data.rows ?? []);
    const activeValues = values[activeCountry];
    if (!activeValues) return;

    const nextRates = computeNextRates({
      existing: existingRates,
      baseline,
      country: activeCountry,
      channel: submittedChannel,
      values: activeValues,
    });

    submit(nextRates)
      .unwrap()
      .then(() => {
        // Only on success, so a failed save keeps the user's edits.
        setSavedAt((count) => count + 1);
        toast.success(messages.success);
      })
      .catch((error: unknown) => toast.error(isPricingConflict(error) ? messages.conflict : messages.error));
  };

  return { activeCountry, setActiveCountry: setSelectedCountry, submitHandler, savedAt };
}
